<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import SchoolCreateForm, {
  type SchoolCreateFormData,
} from '@/components/admin/SchoolCreateForm.vue'
import { useEnvironment } from '@/composables/useEnvironment'
import { useCopySchool } from '@/composables/api/useSchoolsApi'
import type { ServiceType } from '@/constants/environments'

interface Props {
  id: string
}

const props = defineProps<Props>()

const router = useRouter()
const { currentService } = useEnvironment()

const { mutateAsync, isPending, error } = useCopySchool(
  currentService.value.key as ServiceType,
)

const errorMessage = computed(() => error.value?.message ?? null)

const initialData = computed<Partial<SchoolCreateFormData>>(() => ({}))

// Whether to skip copying the source school's logo (backend #269).
const skipLogo = ref(false)

async function handleSubmit(data: SchoolCreateFormData) {
  try {
    await mutateAsync({
      source_school_id: props.id,
      new_school_name: data.name,
      skip_logo: skipLogo.value,
    })
    router.push({ name: 'schools' })
  } catch {
    // Error already surfaced via toast inside the composable.
  }
}

function handleCancel() {
  router.push({ name: 'schools' })
}
</script>

<template>
  <AdminLayout>
    <div class="school-copy">
      <div class="school-copy__header">
        <h2 class="school-copy__heading">Copy School</h2>
        <p class="school-copy__subtitle">
          Copying from school ID: <code>{{ id }}</code>
        </p>
      </div>
      <label class="school-copy__skip-logo">
        <input v-model="skipLogo" type="checkbox" />
        Do not copy the source school's logo
      </label>
      <SchoolCreateForm
        :initial-data="initialData"
        submit-label="Copy School"
        :is-pending="isPending"
        :error="errorMessage"
        @submit="handleSubmit"
        @cancel="handleCancel"
      />
    </div>
  </AdminLayout>
</template>

<style scoped>
.school-copy {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
}

.school-copy__heading {
  font-size: 20px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
}

.school-copy__subtitle {
  font-size: 14px;
  color: #78716c;
  margin: 4px 0 0;
}

.school-copy__subtitle code {
  background: #f5f5f4;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.school-copy__skip-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #44403c;
  cursor: pointer;
}
</style>
